import { isLicensed } from '@/utils/licenseStatus';
import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
   updateDevice,
   listSessions,
   resetDeviceStats,
   type ApiVendoDevice,
   type ApiVendoSession,
} from '@/api/vendo';
import RevenueAreaChart from '@/components/Admin/UI/RevenueAreaChart';
import { SessionTable, mapApiSessionToDisplay } from '@/pages/Admin/Vendo';
import { type ClientProfile } from '@/api/clientPortal';
import {
   Section,
   SectionTitle,
   Hint,
   Pill,
   GhostBtn,
   SmallBtn,
   DangerSmallBtn,
   FormActions,
   FormInput,
} from './shared';

/**
 * Basic portal — Dashboard tab: the same at-a-glance info an operator
 * gets, scoped to the buyer's one device. Sales cards, a daily sales
 * chart, recent sessions and the device health card (editable name).
 */

function timeAgo(iso?: string | null): string {
   if (!iso) return 'never';
   const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
   if (s < 90) return 'just now';
   if (s < 3600) return `${Math.floor(s / 60)} min ago`;
   if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
   return `${Math.floor(s / 86400)} days ago`;
}

const Dashboard: React.FC<{
   device: ApiVendoDevice;
   profile: ClientProfile;
   onChanged: () => void;
}> = ({ device, profile, onChanged }) => {
   const queryClient = useQueryClient();
   const canRename = profile.permissions?.renameVendo !== false;

   // Reset sales (moved here from Settings — Ray: less confusing).
   const [confirmingReset, setConfirmingReset] = useState(false);
   const [resetBusy, setResetBusy] = useState(false);
   const doReset = async () => {
      setResetBusy(true);
      try {
         await resetDeviceStats(device._id);
         setConfirmingReset(false);
         toast.success('Sales reset to zero');
         onChanged();
      } catch (e) {
         toast.error('Could not reset');
      } finally {
         setResetBusy(false);
      }
   };

   const { data: sessionsData } = useQuery({
      queryKey: ['client-sessions'],
      queryFn: () => listSessions(),
      refetchInterval: 60_000,
   });
   // Same table the operator dashboard uses (read-only — no kick/pause).
   const deviceNameById = new Map([[device._id, device.name]]);
   const recent = ((sessionsData?.sessions ?? []) as ApiVendoSession[])
      .slice(0, 5)
      .map((s) => mapApiSessionToDisplay(s, deviceNameById));

   // Editable vendo name
   const [editingName, setEditingName] = useState(false);
   const [nameDraft, setNameDraft] = useState(device.name || '');
   const [savingName, setSavingName] = useState(false);
   const saveName = async () => {
      const trimmed = nameDraft.trim();
      if (!trimmed) return;
      setSavingName(true);
      try {
         await updateDevice(device._id, { name: trimmed });
         toast.success('Name saved');
         setEditingName(false);
         queryClient.invalidateQueries({ queryKey: ['client-device'] });
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not save the name');
      } finally {
         setSavingName(false);
      }
   };

   return (
      <>
         <SalesGrid>
            <SalesCard>
               <SalesLabel>Today</SalesLabel>
               <SalesValue>₱{device.stats?.todayCoins ?? 0}</SalesValue>
            </SalesCard>
            <SalesCard>
               <SalesLabel>Total sales</SalesLabel>
               <SalesValue>₱{device.stats?.totalCoins ?? 0}</SalesValue>
            </SalesCard>
            <SalesCard>
               {/* Survives "Reset sales" — same figure as the operator
                   dashboard's Lifetime Revenue stat. */}
               <SalesLabel>Lifetime</SalesLabel>
               <SalesValue>₱{(device.stats?.lifetimeCoins ?? 0).toLocaleString()}</SalesValue>
            </SalesCard>
            <SalesCard>
               <SalesLabel>Online</SalesLabel>
               <SalesValue>₱{device.stats?.gcashRevenue ?? 0}</SalesValue>
            </SalesCard>
            <SalesCard>
               <SalesLabel>Connected now</SalesLabel>
               <SalesValue>{device.stats?.activeSessions ?? 0}</SalesValue>
            </SalesCard>
         </SalesGrid>

         <RevenueAreaChart title="📈 Daily sales" height={200} />

         <Section>
            {/* Operator-card look (Ray's screenshot): trial banner, name +
                chips, status pills, id · heartbeat, firmware pills, stats. */}
            {device.licenseStatus === 'trial' && (
               <TrialBanner>
                  ⏱ Trial
                  {device.trialEndsAt
                     ? ` · ${Math.max(
                          0,
                          Math.ceil(
                             (new Date(device.trialEndsAt).getTime() - Date.now()) /
                                86400000
                          )
                       )} days left`
                     : ''}
                  <TrialNote>ask your seller to activate</TrialNote>
               </TrialBanner>
            )}
            <CardNameRow>
               {editingName ? (
                  <>
                     <FormInput
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        maxLength={60}
                        autoFocus
                        style={{ maxWidth: 220 }}
                     />
                     <SmallBtn type="button" onClick={saveName} disabled={savingName}>
                        Save
                     </SmallBtn>
                     <GhostBtn type="button" onClick={() => setEditingName(false)}>
                        Cancel
                     </GhostBtn>
                  </>
               ) : (
                  <>
                     <CardName>{device.name}</CardName>
                     <ChipBlue>MAIN</ChipBlue>
                     <ChipGray>
                        {device.router?.platform === 'openwrt' ? 'Ruijie' : 'MikroTik'}
                     </ChipGray>
                     {canRename && (
                        <GhostBtn
                           type="button"
                           onClick={() => {
                              setNameDraft(device.name || '');
                              setEditingName(true);
                           }}
                        >
                           Edit
                        </GhostBtn>
                     )}
                  </>
               )}
            </CardNameRow>
            <CardPillRow>
               {device.updateState && device.updateState !== 'idle' && (
                  <Pill $tone="warn">
                     {device.updateState === 'rebooting'
                        ? '🔄 Rebooting'
                        : '⏳ Updating'}
                  </Pill>
               )}
               <Pill $tone={device.routerStatus === 'connected' ? 'ok' : 'bad'}>
                  ●{' '}
                  {device.routerStatus === 'connected'
                     ? 'Connected'
                     : device.routerStatus === 'agent-down'
                     ? 'Needs restart'
                     : 'Disconnected'}
               </Pill>
               <Pill $tone={device.status === 'online' ? 'ok' : 'bad'}>
                  ● {device.status === 'online' ? 'Online' : 'Offline'}
               </Pill>
               <Pill
                  $tone={
                     isLicensed(device.licenseStatus)
                        ? 'ok'
                        : device.licenseStatus === 'trial'
                        ? 'warn'
                        : 'bad'
                  }
               >
                  {device.licenseStatus === 'lifetime'
                     ? 'Lifetime license'
                     : isLicensed(device.licenseStatus)
                     ? 'Licensed'
                     : device.licenseStatus}
               </Pill>
            </CardPillRow>
            <CardMetaRow>
               <MetaMono>{device.deviceId}</MetaMono> ·{' '}
               {timeAgo(device.lastHeartbeat)}
               {device.router?.firmwareVersion && (
                  <ChipGray>Router {device.router.firmwareVersion.split('-')[0]}</ChipGray>
               )}
               {device.nodeMcu?.firmwareVersion && (
                  <ChipGray>NodeMCU {device.nodeMcu.firmwareVersion}</ChipGray>
               )}
            </CardMetaRow>
            <CardStatsRow>
               <div>
                  <StatLabel>Total Today</StatLabel>
                  <StatValue>₱{device.stats?.todayCoins ?? 0}</StatValue>
               </div>
               <div>
                  <StatLabel>Revenue</StatLabel>
                  <StatValue>₱{device.stats?.totalCoins ?? 0}</StatValue>
               </div>
               <div>
                  <StatLabel>Active</StatLabel>
                  <StatValue>{device.stats?.activeSessions ?? 0}</StatValue>
               </div>
            </CardStatsRow>
            <ResetRow>
               {confirmingReset ? (
                  <FormActions>
                     <DangerSmallBtn type="button" onClick={doReset} disabled={resetBusy}>
                        {resetBusy ? 'Resetting…' : 'Yes, reset sales to zero'}
                     </DangerSmallBtn>
                     <GhostBtn type="button" onClick={() => setConfirmingReset(false)}>
                        Cancel
                     </GhostBtn>
                  </FormActions>
               ) : (
                  <GhostBtn type="button" onClick={() => setConfirmingReset(true)}>
                     🔄 Reset sales…
                  </GhostBtn>
               )}
            </ResetRow>
         </Section>

         <Section>
            <SectionTitle>🕒 Recent sessions</SectionTitle>
            {recent.length === 0 ? (
               <Hint>No sessions yet.</Hint>
            ) : (
               <SessionTable sessions={recent} />
            )}
         </Section>
      </>
   );
};

export default Dashboard;

const SalesGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, 1fr);
   gap: 8px;
   @media (min-width: 700px) {
      grid-template-columns: repeat(5, 1fr);
   }
`;

const SalesCard = styled.div`
   background: var(--app-surface, #fff);
   border: 1px solid var(--app-border, #e3e8ef);
   border-radius: 12px;
   padding: 12px 14px;
`;

const SalesLabel = styled.div`
   font-size: 11.5px;
   font-weight: 600;
   color: var(--app-text-muted, #7b8794);
`;

const SalesValue = styled.div`
   margin-top: 2px;
   font-size: 20px;
   font-weight: 800;
`;









const TrialBanner = styled.div`
   background: #fff8e6;
   border: 1px solid #f0d9a8;
   border-radius: 10px;
   padding: 9px 12px;
   font-size: 12.5px;
   font-weight: 700;
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 10px;
`;

const TrialNote = styled.span`
   margin-left: auto;
   font-size: 11px;
   font-weight: 600;
   color: var(--app-text-muted, #7b8794);
`;

const CardNameRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const CardName = styled.h2`
   font-size: 18px;
   font-weight: 800;
`;

const ChipBlue = styled.span`
   background: #4f7cff18;
   color: #4f7cff;
   border-radius: 7px;
   padding: 2px 8px;
   font-size: 10.5px;
   font-weight: 800;
`;

const ChipGray = styled.span`
   background: var(--app-border, #e3e8ef);
   color: var(--app-text-dim, #55606c);
   border-radius: 7px;
   padding: 2px 8px;
   font-size: 10.5px;
   font-weight: 700;
`;

const CardPillRow = styled.div`
   margin-top: 10px;
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
`;

const CardMetaRow = styled.div`
   margin-top: 10px;
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
   font-size: 12px;
   color: var(--app-text-dim, #55606c);
`;

const MetaMono = styled.span`
   font-family: ui-monospace, monospace;
   font-weight: 700;
`;

const CardStatsRow = styled.div`
   margin-top: 12px;
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
   border-top: 1px solid var(--app-border, #e3e8ef);
   padding-top: 12px;
`;

const StatLabel = styled.div`
   font-size: 11.5px;
   color: var(--app-text-muted, #7b8794);
`;

const StatValue = styled.div`
   font-size: 19px;
   font-weight: 800;
`;

const ResetRow = styled.div`
   margin-top: 10px;
`;
